/**
 * AI Platform
 * Chat API of the hosted agent server (`@convercus/agent-server`). Read a chat as a view: a snapshot from `GET /chats/{chatId}/view`, then its changes from the NDJSON stream `GET /chats/{chatId}/view/stream?after` or the long-poll `GET /chats/{chatId}/view?after&waitMs`. The service sits behind the API gateway, which validates the JWT and forwards identity as plain headers — `program`, `user-id`, `username` and the user\'s `roles`. The view stream is the exception: it is reached without the gateway and verifies the `Authorization: Bearer` token itself. With `AUTH_MODE=mock` (local dev) every request runs as a canned identity and the headers are ignored. Every route but health names the permission it needs and answers 403 to a role that grants none of them: `agent.bi` (ANALYTICS, SUPER_ADMIN) for agents and chats; `agent.config.read`/`agent.config.write` (PROGRAM_MANAGER, SUPER_ADMIN) for the settings; dedicated mode and the spend ledger are SUPER_ADMIN only.
 *
 * The version of the OpenAPI document: 1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

import { AbortTaskResult } from '../model/models';
import { AgentBudgetExceeded } from '../model/models';
import { AgentChat } from '../model/models';
import { AgentChatWithNotice } from '../model/models';
import { AnswerRequest } from '../model/models';
import { CancelRequest } from '../model/models';
import { CancelResult } from '../model/models';
import { ChatFeedbackList } from '../model/models';
import { ChatFeedbackRequest } from '../model/models';
import { ChatHistoryPage } from '../model/models';
import { ChatViewPoll } from '../model/models';
import { PostMessageRequest } from '../model/models';
import { RenameChatRequest } from '../model/models';
import { SendMessageAccepted } from '../model/models';
import { SendNowRequest } from '../model/models';
import { SendNowResult } from '../model/models';
import { SetChatAgentRequest } from '../model/models';
import { SetChatModeRequest } from '../model/models';

import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    ChatsServiceInterface,
    AbortChatTaskRequestParams,
    AnswerQuestionRequestParams,
    CancelChatRequestParams,
    GetChatHistoryRequestParams,
    GetChatViewRequestParams,
    ListChatFeedbackRequestParams,
    RateAnswerRequestParams,
    RenameChatRequestParams,
    SendMessageRequestParams,
    SendQueuedNowRequestParams,
    SetChatAgentRequestParams,
    SetChatModeRequestParams,
    StreamChatViewRequestParams
} from './chats.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class ChatsService implements ChatsServiceInterface {

    protected basePath = 'http://localhost';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key,
                        (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Abort a task pi reports as blocked
     * &#x60;agent.ops.abort&#x60;, SUPER_ADMIN only. Aborts one task of the chat when pi reports it blocked (no registered definition takes it: missing_task, task_too_old, migration_failed); pi settles it as orphaned and the chat can idle and close. 404 for an unknown or archived chat, a chat of another program (log in to its realm; &#x60;pi.task.blocked&#x60; names the program) or an unknown task; 409 &#x60;not_blocked&#x60; for a task in any other state (use cancel for a running answer).
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public abortChatTask(requestParameters: AbortChatTaskRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<AbortTaskResult>;
    public abortChatTask(requestParameters: AbortChatTaskRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<AbortTaskResult>>;
    public abortChatTask(requestParameters: AbortChatTaskRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<AbortTaskResult>>;
    public abortChatTask(requestParameters: AbortChatTaskRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const taskId = requestParameters.taskId;
        if (taskId === null || taskId === undefined) {
            throw new Error('Required parameter taskId was null or undefined when calling abortChatTask.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling abortChatTask.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<AbortTaskResult>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/tasks/${encodeURIComponent(String(taskId))}/abort`,
            null,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Answer the question a running tool call asked
     * Answers the question in &#x60;pi.live.tools[i].details.question&#x60; of the slot with this &#x60;taskId&#x60;, for the pause in &#x60;details.pauseSeq&#x60;. The first answer wins (409 after it, also for a pause the call already left); 410 once the call is over (answered elsewhere, timed out, cancelled) or for a pause it never reached.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public answerQuestion(requestParameters: AnswerQuestionRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<any>;
    public answerQuestion(requestParameters: AnswerQuestionRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpResponse<any>>;
    public answerQuestion(requestParameters: AnswerQuestionRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpEvent<any>>;
    public answerQuestion(requestParameters: AnswerQuestionRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined}): Observable<any> {
        const answerRequest = requestParameters.answerRequest;
        if (answerRequest === null || answerRequest === undefined) {
            throw new Error('Required parameter answerRequest was null or undefined when calling answerQuestion.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling answerQuestion.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<any>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/answers`,
            answerRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Stop the running answer, or withdraw a queued message
     * Without &#x60;submissionId&#x60;: stops the running answer; queued follow-ups stay queued. With it: withdraws that queued message. 409 when nothing is running.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public cancelChat(requestParameters: CancelChatRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<CancelResult>;
    public cancelChat(requestParameters: CancelChatRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<CancelResult>>;
    public cancelChat(requestParameters: CancelChatRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<CancelResult>>;
    public cancelChat(requestParameters: CancelChatRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const cancelRequest = requestParameters.cancelRequest;
        if (cancelRequest === null || cancelRequest === undefined) {
            throw new Error('Required parameter cancelRequest was null or undefined when calling cancelChat.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling cancelChat.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<CancelResult>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/cancel`,
            cancelRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Read entries older than the view
     * The view holds the active context (from the newest compaction or reset). Older entries come from here, newest first, &#x60;limit&#x60; (1–100, default 50) per page, redacted like the view.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getChatHistory(requestParameters: GetChatHistoryRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ChatHistoryPage>;
    public getChatHistory(requestParameters: GetChatHistoryRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ChatHistoryPage>>;
    public getChatHistory(requestParameters: GetChatHistoryRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ChatHistoryPage>>;
    public getChatHistory(requestParameters: GetChatHistoryRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling getChatHistory.');
        }
        const limit = requestParameters.limit;
        const before = requestParameters.before;

        let queryParameters = new HttpParams({encoder: this.encoder});
        if (limit !== undefined && limit !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>limit, 'limit');
        }
        if (before !== undefined && before !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>before, 'before');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ChatHistoryPage>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/history`,
            {
                params: queryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Read the chat view, or wait for its next frames
     * Without &#x60;after&#x60;: a snapshot &#x60;{cursor, view}&#x60;. With &#x60;after&#x60;: the frames after that cursor, waiting up to &#x60;waitMs&#x60; (at most 8000) for the next one, or &#x60;{reset}&#x60; when the cursor cannot be resumed. Apply each frame’s &#x60;ops&#x60; in order. A chat nobody owns waits the same &#x60;waitMs&#x60; and answers &#x60;closed: \&quot;idle\&quot;&#x60;; a message sent meanwhile ends the wait with a &#x60;reset&#x60;.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getChatView(requestParameters: GetChatViewRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ChatViewPoll>;
    public getChatView(requestParameters: GetChatViewRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ChatViewPoll>>;
    public getChatView(requestParameters: GetChatViewRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ChatViewPoll>>;
    public getChatView(requestParameters: GetChatViewRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling getChatView.');
        }
        const waitMs = requestParameters.waitMs;
        const after = requestParameters.after;

        let queryParameters = new HttpParams({encoder: this.encoder});
        if (waitMs !== undefined && waitMs !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>waitMs, 'waitMs');
        }
        if (after !== undefined && after !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>after, 'after');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ChatViewPoll>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/view`,
            {
                params: queryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * The chat\&#39;s ratings, one per rated answer
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public listChatFeedback(requestParameters: ListChatFeedbackRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ChatFeedbackList>;
    public listChatFeedback(requestParameters: ListChatFeedbackRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ChatFeedbackList>>;
    public listChatFeedback(requestParameters: ListChatFeedbackRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ChatFeedbackList>>;
    public listChatFeedback(requestParameters: ListChatFeedbackRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling listChatFeedback.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ChatFeedbackList>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/feedback`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Rate an answer
     * Thumbs up or down (and an optional comment) on a &#x60;pi.assistant&#x60; entry. Rating again replaces it.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public rateAnswer(requestParameters: RateAnswerRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<any>;
    public rateAnswer(requestParameters: RateAnswerRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpResponse<any>>;
    public rateAnswer(requestParameters: RateAnswerRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpEvent<any>>;
    public rateAnswer(requestParameters: RateAnswerRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined}): Observable<any> {
        const chatFeedbackRequest = requestParameters.chatFeedbackRequest;
        if (chatFeedbackRequest === null || chatFeedbackRequest === undefined) {
            throw new Error('Required parameter chatFeedbackRequest was null or undefined when calling rateAnswer.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling rateAnswer.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<any>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/feedback`,
            chatFeedbackRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Rename a chat
     * Sets the display title (trimmed, 1–120 chars) and marks titleSource \&quot;user\&quot; — permanent, auto-naming never overwrites it.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public renameChat(requestParameters: RenameChatRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<AgentChat>;
    public renameChat(requestParameters: RenameChatRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<AgentChat>>;
    public renameChat(requestParameters: RenameChatRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<AgentChat>>;
    public renameChat(requestParameters: RenameChatRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const renameChatRequest = requestParameters.renameChatRequest;
        if (renameChatRequest === null || renameChatRequest === undefined) {
            throw new Error('Required parameter renameChatRequest was null or undefined when calling renameChat.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling renameChat.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.patch<AgentChat>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}`,
            renameChatRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Send a user message
     * Admits the message into the chat: it starts an answer, or queues behind the running one (&#x60;whenBusy&#x60;). Follow the answer with GET /chats/{chatId}/view.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public sendMessage(requestParameters: SendMessageRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<SendMessageAccepted>;
    public sendMessage(requestParameters: SendMessageRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<SendMessageAccepted>>;
    public sendMessage(requestParameters: SendMessageRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<SendMessageAccepted>>;
    public sendMessage(requestParameters: SendMessageRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const postMessageRequest = requestParameters.postMessageRequest;
        if (postMessageRequest === null || postMessageRequest === undefined) {
            throw new Error('Required parameter postMessageRequest was null or undefined when calling sendMessage.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling sendMessage.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<SendMessageAccepted>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/messages`,
            postMessageRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Send a queued message now
     * While an answer runs, the queued message becomes a steer: it joins the running answer after its current tool round. On an idle chat (after Stop, or a failed answer) it is placed and a new answer starts; when several messages wait, the oldest goes with it.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public sendQueuedNow(requestParameters: SendQueuedNowRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<SendNowResult>;
    public sendQueuedNow(requestParameters: SendQueuedNowRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<SendNowResult>>;
    public sendQueuedNow(requestParameters: SendQueuedNowRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<SendNowResult>>;
    public sendQueuedNow(requestParameters: SendQueuedNowRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const sendNowRequest = requestParameters.sendNowRequest;
        if (sendNowRequest === null || sendNowRequest === undefined) {
            throw new Error('Required parameter sendNowRequest was null or undefined when calling sendQueuedNow.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling sendQueuedNow.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<SendNowResult>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/send-now`,
            sendNowRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Set the chat\&#39;s model and reasoning level
     * Either or both. Applies from the next model request, also in a running answer. A model the program may not use is a 422.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public setChatAgent(requestParameters: SetChatAgentRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<AgentChatWithNotice>;
    public setChatAgent(requestParameters: SetChatAgentRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<AgentChatWithNotice>>;
    public setChatAgent(requestParameters: SetChatAgentRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<AgentChatWithNotice>>;
    public setChatAgent(requestParameters: SetChatAgentRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const setChatAgentRequest = requestParameters.setChatAgentRequest;
        if (setChatAgentRequest === null || setChatAgentRequest === undefined) {
            throw new Error('Required parameter setChatAgentRequest was null or undefined when calling setChatAgent.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling setChatAgent.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.put<AgentChatWithNotice>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/agent`,
            setChatAgentRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Set the chat\&#39;s access mode
     * Sets the sticky access mode (\&quot;accept-all\&quot; | \&quot;ask\&quot; | \&quot;view\&quot;). The next tool call and approval follow it, also in a running answer.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public setChatMode(requestParameters: SetChatModeRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<AgentChat>;
    public setChatMode(requestParameters: SetChatModeRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<AgentChat>>;
    public setChatMode(requestParameters: SetChatModeRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<AgentChat>>;
    public setChatMode(requestParameters: SetChatModeRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const setChatModeRequest = requestParameters.setChatModeRequest;
        if (setChatModeRequest === null || setChatModeRequest === undefined) {
            throw new Error('Required parameter setChatModeRequest was null or undefined when calling setChatMode.');
        }
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling setChatMode.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.put<AgentChat>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/mode`,
            setChatModeRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Follow the chat view as an NDJSON stream
     * Chunked &#x60;application/x-ndjson&#x60;, one JSON object per line: &#x60;{\&quot;reset\&quot;:{cursor, view}}&#x60; first when &#x60;after&#x60; is missing or cannot be resumed; &#x60;{\&quot;cursor\&quot;:\&quot;E.n\&quot;,\&quot;frame\&quot;:{n, ops}}&#x60; per commit (apply the ops in order); &#x60;{\&quot;hb\&quot;:1}&#x60; every 20 s; &#x60;{\&quot;closed\&quot;:\&quot;&lt;reason&gt;\&quot;}&#x60; before the server ends it (the chat closed, is idle with no owner after an 8 s wait, or the stream is rotated: reconnect at once only on &#x60;rotate&#x60;, back off otherwise). Reconnect with &#x60;after&#x60; &#x3D; the last cursor; on errors fall back to the long-poll. Errors before the first line are JSON as elsewhere. Served straight from the load balancer, not the API gateway: send the user\&#39;s token as &#x60;Authorization: Bearer&#x60;; the identity headers are ignored here.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public streamChatView(requestParameters: StreamChatViewRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/x-ndjson'}): Observable<string>;
    public streamChatView(requestParameters: StreamChatViewRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/x-ndjson'}): Observable<HttpResponse<string>>;
    public streamChatView(requestParameters: StreamChatViewRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/x-ndjson'}): Observable<HttpEvent<string>>;
    public streamChatView(requestParameters: StreamChatViewRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/x-ndjson'}): Observable<any> {
        const chatId = requestParameters.chatId;
        if (chatId === null || chatId === undefined) {
            throw new Error('Required parameter chatId was null or undefined when calling streamChatView.');
        }
        const after = requestParameters.after;

        let queryParameters = new HttpParams({encoder: this.encoder});
        if (after !== undefined && after !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>after, 'after');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/x-ndjson'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<string>(`${this.configuration.basePath}/ai-platform-service/chats/${encodeURIComponent(String(chatId))}/view/stream`,
            {
                params: queryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
